<template>
  <b-row cols="1" cols-sm="1" cols-md="1" cols-lg="1" cols-xl="1">
    <b-col>
      <div class="center-form">
        <h2 class="center-align">Deploy new app</h2>
        <div v-if="user.hasGithubAccessToken && !userMustReconnectGithub">
          <b-form>
            <b-form-group id="name" label="Enter app name" label-for="name" description="Name must be unique and can only contain lowercase letters, numbers and dashes">
              <b-form-input 
                id="name"
                v-model="form.name.value"
                :state="form.name.error.state"
                placeholder="Example: my-app-123"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.name.error.state">
                {{ form.name.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="regions" label="Choose region(s)" label-for="regions" description="Don't find your region? Contact us on the chat">
              <div v-if="environment === 'prod'">
                <b-form-checkbox 
                  v-model="form.regions.value"
                  name="france-radio"
                  value="FRANCE"
                >
                  France
                </b-form-checkbox>
              </div>
              <div v-else>
                <b-form-checkbox 
                  v-model="form.regions.value"
                  name="local-radio"
                  value="LOCAL"
                >
                  Local
                </b-form-checkbox>
                <b-form-checkbox 
                  v-model="form.regions.value"
                  name="local2-radio"
                  value="LOCAL2"
                >
                  Local 2
                </b-form-checkbox>
              </div>
              <b-form-invalid-feedback :state="form.regions.error.state">
                {{ form.regions.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="size" label-for="size" description="Need bigger instances? Contact us on the chat">
              Choose size (<router-link :to="{ name: 'Pricing'}" target="_blank">view pricing</router-link>)
              <br>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="xs-radio"
                value="128"
              >
                XS (128MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="s-radio"
                value="256"
              >
                S (256MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="m-radio"
                value="512"
              >
                M (512MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="l-radio"
                value="1024"
              >
                L (1024MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="xl-radio"
                value="2048"
              >
                XL (2048MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="xxl-radio"
                value="4096"
              >
                XXL (4096MB)
              </b-form-radio>
              <b-form-radio 
                class="inline margin-2"
                v-model="form.size.value"
                name="xxxl-radio"
                value="8192"
              >
                XXXL (8192MB)
              </b-form-radio>
              <b-form-invalid-feedback :state="form.size.error.state">
                {{ form.size.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group class="margin-top-25" id="repo" label="Choose repo" label-for="repo">
              <multiselect
                v-model="form.repo.value"
                :options="repos"
                placeholder="Search for a repo"
                select-label=""
                :multiple="false"
                :searchable="true"
                :hide-selected="false"
              >
              </multiselect>
              <b-form-invalid-feedback :state="form.repo.error.state">
                {{ form.repo.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="branch" :label="`Enter branch to deploy`" label-for="branch">
              <b-form-input
                id="branch"
                v-model="form.branch.value"
                :state="form.branch.error.state"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.branch.error.state">
                {{ form.branch.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="language" label="Choose language" label-for="language">
              <b-form-radio 
                v-model="form.language.value"
                name="language-radio"
                value="node"
              >
                Node.js
              </b-form-radio>
              <b-form-invalid-feedback :state="form.language.error.state">
                {{ form.language.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="version" :label="`Enter ${form.language.value} version`" label-for="version">
              <b-form-input
                id="version"
                v-model="form.version.value"
                :state="form.version.error.state"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.version.error.state">
                {{ form.version.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="folder" label="Enter folder name where app is located" label-for="folder" description="Leave root if app is located at root">
              <b-form-input
                id="folder"
                v-model="form.folder.value"
                :state="form.folder.error.state"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.folder.error.state">
                {{ form.folder.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="install" label="Enter install command" label-for="install">
              <b-form-input
                id="install"
                v-model="form.install.value"
                :state="form.install.error.state"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.install.error.state">
                {{ form.install.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="start" label="Enter start command" label-for="start">
              <b-form-input
                id="start"
                v-model="form.start.value"
                :state="form.start.error.state"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.start.error.state">
                {{ form.start.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="port" label="Enter port (on which your app is listening on)" label-for="port">
              <b-form-input
                id="port"
                v-model="form.port.value"
                :state="form.port.error.state"
                placeholder="Example: 3000"
                type="number"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.port.error.state">
                {{ form.port.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="variables" label="Enter environment variables" label-for="variables" description="Copy paste here the content of your .env file. One variable per line. Don't use any quotes (' or &quot;) around the values">
              <b-form-textarea
                id="variables"
                v-model="form.variables.value"
                :state="form.variables.error.state"
                placeholder=
                'VARIABLE1=test                                                                                                                      VARIABLE2=test2'
                rows="5"
              ></b-form-textarea>
              <b-form-invalid-feedback :state="form.variables.error.state">
                {{ form.variables.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group id="domain" label="Enter domain name that will point to the app" label-for="domain" description="Enter domain without https://">
              <b-form-input
                id="domain"
                v-model="form.domain.value"
                :state="form.domain.error.state"
                placeholder="Example: app.yourdomain.com"
                type="text"
              ></b-form-input>
              <b-form-invalid-feedback :state="form.domain.error.state">
                {{ form.domain.error.value }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-form>

          <c-button :onClick="onCancel" variant="secondary" class="right-align">Cancel</c-button>
          <c-button :onClick="onSubmit" variant="primary">Deploy</c-button>
          <br>
          <span class="success">{{ formSuccess }}</span>
          <span class="failure">{{ formFailure }}</span>
        </div>

        <div v-else class="center-align">
          <c-button :onClick="onConnect" variant="transparent" :keepLoading="true">
            <img class="logo" src="../../public/github.png" width="300px">
          </c-button>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>

  import CButton from '@/components/Button'
  import Multiselect from 'vue-multiselect';

  export default {
    name: 'Deployment',
    components: { CButton, Multiselect },
    props: {

    },
    data() {
      return {
        form: {
          name: {
            value: '',
            error: {}
          },
          regions: {
            value: [],
            error: {}
          },
          size: {
            value: '128',
            error: {}
          },
          repo: {
            value: '',
            error: {}
          },
          branch: {
            value: 'master',
            error: {}
          },
          language: {
            value: 'node',
            error: {}
          },
          version: {
            value: '22.5.1',
            error: {}
          },
          folder: {
            value: 'root',
            error: {}
          },
          install: {
            value: 'npm install',
            error: {}
          },
          start: {
            value: 'npm run start',
            error: {}
          },
          port: {
            value: '',
            error: {}
          },
          variables: {
            value: '',
            error: {}
          },
          domain: {
            value: '',
            error: {}
          },
        },
        formSuccess: '',
        formFailure: '',
        repos: [],
        userMustReconnectGithub: false,
        sizePrice: {
          128: {
            month: 1,
            minute: 0.000023148148
          },
          256: {
            month: 2,
            minute: 0.000046296296
          },
          512: {
            month: 4,
            minute: 0.000092592592
          }, 
          1024: {
            month: 8,
            minute: 0.000185185184
          },
          2048: {
            month: 16,
            minute: 0.000370370368
          },
          4096: {
            month: 32,
            minute: 0.000740740736
          },
          8192: {
            month: 64,
            minute: 0.001481481472
          }
        }
      }
    },
    async created() {
      await this.listUserRepos()
    },
    computed: {
      user() {
        return this.$store.state.loggedUser;
      },
      price() {
        return {
          month: this.sizePrice[this.form.size.value].month * this.form.scale.value,
          minute: this.sizePrice[this.form.size.value].minute * this.form.scale.value
        }
      },
      environment() {
        return process.env.VUE_APP_MODE
      }
    },
    methods: {
      async onConnect(event) {
        const response = await this.$http.get(process.env.VUE_APP_API_BASE_URL + "/github/auth")

        if (response && response.body && response.body.authUrlWeb) {
          window.location.href = response.body.authUrlWeb;
        } else {
          throw(new Error('Could not redirect to Github log in page'))
        }
      },
      async listUserRepos() {
        try {
          const response = await this.$http.get(process.env.VUE_APP_API_BASE_URL + "/github/repos")

          if (response.body && response.body.repos) {
            this.repos = response.body.repos
          }
        } catch(e) {
          if (e.body) {
            if (e.body.message && e.body.message.includes('User must reconnect')) {
              this.userMustReconnectGithub = true
            }
          }
        }
      },
      async onSubmit(event) {
        for (let key in this.form) {
          this.form[key].error = {}
        }
        this.formSuccess = ''
        this.formFailure = ''

        const body = {
          regions: this.form.regions.value,
          name: this.form.name.value,
          size: Number(this.form.size.value),
          repo: this.form.repo.value,
          branch: this.form.branch.value,
          language: this.form.language.value,
          version: this.form.version.value,
          folder: this.form.folder.value,
          install: this.form.install.value,
          start: this.form.start.value,
          port: Number(this.form.port.value),
          variables: this.form.variables.value,
          domain: this.form.domain.value
        }

        const result = await this.$apollo.mutate({
          client: 'private',
          mutation: require('@/graphql/createDeployment.graphql'),
          variables: body
        });

        if (result.errors) {
          const e = result;
          if (e.body) {
            if (e.body.message) {
              this.formFailure = e.body.message;
            }
            if (e.body.details) {
              e.body.details.map(detail => {
                detail.path.map(path => {
                  this.$set(this.form[path], 'error', {state: false, value: detail.message})
                })
              })
            }
          }
        } else {
          if (result && result.data && result.data.createDeployment && result.data.createDeployment.id) {
            this.$router.push({ name: 'Deployment', params: { id: result.data.createDeployment.id, start: true }});
          }
        }
      },
      onCancel(event) {
        this.$router.push({ name: 'Deployments' });
      },
    }
  }
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>

</style>
